import React, { useCallback, useEffect, useState } from 'react';
import { SlidersHorizontal, ToggleLeft, Building2, RefreshCw } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import AdminPlatformTogglesPanel from './AdminPlatformTogglesPanel';
import EmailHealthCard from './EmailHealthCard';

export default function ConfigurationCenter() {
  const [flags, setFlags] = useState(null);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [f, c] = await Promise.all([
        apiCall('/api/admin/feature-flags'),
        apiCall('/api/admin/config-center'),
      ]);
      setFlags(f.flags || []); setConfig(c);
    } catch (e) { toast.error(e.message); setFlags([]); setConfig({}); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleFlag = async (key, enabled) => {
    setBusy(key);
    try {
      await apiCall(`/api/admin/feature-flags/${key}`, { method: 'PUT', body: JSON.stringify({ enabled }) });
      setFlags((prev) => prev.map((f) => (f.key === key ? { ...f, enabled } : f)));
      toast.success(`${key} ${enabled ? 'enabled' : 'disabled'} platform-wide`);
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  if (flags === null || config === null) return <p className="text-sm text-slate-500 py-8">Loading configuration…</p>;
  const orgs = config.organizations || [];
  return (
    <div className="space-y-6" data-testid="configuration-center">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <SlidersHorizontal className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Configuration Center</h2>
            <p className="text-xs text-slate-500">Feature flags & every organization policy — no code changes required</p>
          </div>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium" data-testid="config-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      <EmailHealthCard />

      {/* Feature flags */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-1.5"><ToggleLeft className="w-4 h-4 text-indigo-500" /> Platform Feature Flags</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {flags.map((f) => (
            <div key={f.key} className="flex items-start justify-between gap-3 border border-slate-200 rounded-xl p-3" data-testid={`flag-card-${f.key}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{f.label}</p>
                <p className="text-xs text-slate-500">{f.description}</p>
                {Object.keys(f.org_overrides || {}).length > 0 && (
                  <p className="text-[11px] text-violet-600 mt-1">{Object.keys(f.org_overrides).length} org override(s)</p>
                )}
              </div>
              <button onClick={() => toggleFlag(f.key, !f.enabled)} disabled={busy === f.key}
                className={`shrink-0 relative w-11 h-6 rounded-full transition-colors ${f.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                data-testid={`flag-toggle-${f.key}`} aria-label={`Toggle ${f.label}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${f.enabled ? 'left-5.5 translate-x-0 right-0.5' : 'left-0.5'}`} style={{ left: f.enabled ? '22px' : '2px' }} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Platform toggles (moved here from the former Platform Toggles tab) */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="config-platform-toggles">
        <AdminPlatformTogglesPanel embedded />
      </div>

      {/* Platform settings snapshot */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Platform Security Settings</p>
        <div className="flex flex-wrap gap-4 text-xs text-slate-600" data-testid="config-platform-settings">
          {Object.entries(config.platform_settings || {}).map(([k, v]) => (
            <span key={k} className="px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
              <span className="text-slate-400">{k.replace(/_/g, ' ')}:</span> <b>{String(v)}</b>
            </span>
          ))}
          {!Object.keys(config.platform_settings || {}).length && <span className="text-slate-400">No platform settings document.</span>}
        </div>
      </div>

      {/* Org policy explorer */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-indigo-500" /> Organization Policies ({orgs.length})</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="config-org-table">
            <thead className="text-slate-400">
              <tr><th className="text-left py-1.5 font-medium">Organization</th><th className="text-left font-medium">Role</th><th className="text-right font-medium">Wk limit</th><th className="text-left font-medium pl-3">AI mode</th><th className="text-right font-medium">Min score</th><th className="text-center font-medium">Fairness</th><th className="text-center font-medium">Balance</th><th className="text-center font-medium">Sharing</th><th className="text-left font-medium pl-3">Region</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orgs.map((o) => (
                <tr key={o.org_id} data-testid={`config-org-${o.org_id}`}>
                  <td className="py-2 font-medium text-slate-800">{o.name}</td>
                  <td className="text-slate-500">{o.role}</td>
                  <td className="text-right text-slate-600">{o.weekly_hour_limit}h</td>
                  <td className="pl-3"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${o.ai_mode === 'auto' ? 'bg-violet-100 text-violet-700' : o.ai_mode === 'suggest' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>{o.ai_mode}</span></td>
                  <td className="text-right text-slate-600">{o.ai_min_score}</td>
                  <td className="text-center">{o.fairness ? '✓' : '—'}</td>
                  <td className="text-center">{o.balance ? '✓' : '—'}</td>
                  <td className="text-center">{o.sharing_enabled ? <span className="text-emerald-600 font-bold">on</span> : '—'}</td>
                  <td className="pl-3 text-slate-500">{o.region || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
